import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  FormHelperText,
  Card,
  CardContent,
  IconButton,
  CardMedia,
  Chip,
  CircularProgress,
  InputAdornment,
  Divider,
  Tabs,
  Tab,

  Tooltip,

  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import TitleIcon from '@mui/icons-material/Title';
import ImageIcon from '@mui/icons-material/Image';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { createPost, generateTagSuggestions } from '../services/api';

// Styling for file input
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Blog categories
const CATEGORIES = [
  { name: 'Scholarship', color: '#9C27B0' }, // Purple
  { name: 'Education', color: '#357a38' },   // Green
  { name: 'Admissions', color: '#C6FF00' },  // Lime
];

// Common tag suggestions
const COMMON_TAGS = [
  'JavaScript', 'React', 'Web Development', 'Frontend', 'Backend',
  'Python', 'Machine Learning', 'AI', 'Data Science',
  'Tutorial', 'Guide', 'Tips', 'Best Practices'
];

// Updated styles for page sections
const SectionBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  backgroundColor: '#ffffff',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    backgroundColor: theme.palette.primary.main,
  }
}));

// Enhanced section header
const SectionHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1.5),
    color: theme.palette.primary.main,
  }
}));

function PostCreate() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const contentRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
    tags: [],
    category: '',
    thumbnail: null, // For thumbnail image file
    images: [], // For content images
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [tagAnchorEl, setTagAnchorEl] = useState(null);

  const [editorTab, setEditorTab] = useState(0);
  const [cursorPosition, setCursorPosition] = useState({ start: 0, end: 0 });
  const [tagSuggestionsLoading, setTagSuggestionsLoading] = useState(false);

  // Track cursor position in the content textarea
  useEffect(() => {
    const handleSelectionChange = () => {
      if (contentRef.current) {
        setCursorPosition({
          start: contentRef.current.selectionStart,
          end: contentRef.current.selectionEnd
        });
      }
    };

    const textArea = contentRef.current;
    if (textArea) {
      textArea.addEventListener('mouseup', handleSelectionChange);
      textArea.addEventListener('keyup', handleSelectionChange);
      textArea.addEventListener('click', handleSelectionChange);
    }

    return () => {
      if (textArea) {
        textArea.removeEventListener('mouseup', handleSelectionChange);
        textArea.removeEventListener('keyup', handleSelectionChange);
        textArea.removeEventListener('click', handleSelectionChange);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Generate tag suggestions when content changes
    if (name === 'content' && value.length > 50) {
      const debounceTimer = setTimeout(() => {
        generateTagsFromContent(value);
      }, 1000);

      return () => clearTimeout(debounceTimer);
    }
  };

  // Generate tag suggestions based on content
  const generateTagsFromContent = async (content) => {
    try {
      setTagSuggestionsLoading(true);
      const suggestions = await generateTagSuggestions(content);
      setSuggestedTags(prev => {
        const newTags = suggestions.filter(tag =>
          !formData.tags.includes(tag) && !prev.includes(tag)
        );
        return [...prev, ...newTags];
      });
    } catch (error) {
      console.error('Error generating tag suggestions:', error);
    } finally {
      setTagSuggestionsLoading(false);
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    } else if (formData.tags.includes(tag)) {
      enqueueSnackbar('Tag already exists', { variant: 'info' });
    }
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToDelete)
    }));
  };

  // Tag suggestion menu
  const handleTagMenuOpen = (event) => {
    setTagAnchorEl(event.currentTarget);
  };

  const handleTagMenuClose = () => {
    setTagAnchorEl(null);
  };

  const handleSuggestedTagSelect = (tag) => {
    if (!formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
    // Remove from suggestions
    setSuggestedTags(prev => prev.filter(t => t !== tag));
    handleTagMenuClose();
  };

  // Category handling
  const handleCategoryChange = (event) => {
    setFormData(prev => ({
      ...prev,
      category: event.target.value
    }));

    // Clear error when field is edited
    if (errors.category) {
      setErrors(prev => ({ ...prev, category: '' }));
    }
  };



  // Thumbnail Image handling
  const handleThumbnailUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFormData(prev => {
      // Revoke previous object URL if exists to avoid memory leaks
      if (prev.thumbnail?.preview) {
        URL.revokeObjectURL(prev.thumbnail.preview);
      }

      return {
        ...prev,
        thumbnail: {
          file,
          preview: URL.createObjectURL(file),
          name: file.name
        }
      };
    });
  };

  const handleRemoveThumbnail = () => {
    setFormData(prev => {
      // Revoke object URL to avoid memory leaks
      if (prev.thumbnail?.preview) {
        URL.revokeObjectURL(prev.thumbnail.preview);
      }

      return {
        ...prev,
        thumbnail: null
      };
    });
  };

  // Content Image handling
  const handleImageUpload = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newImages = Array.from(files).map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));

    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }));
  };

  const handleRemoveImage = (indexToRemove) => {
    setFormData(prev => {
      // Revoke object URL to avoid memory leaks
      URL.revokeObjectURL(prev.images[indexToRemove].preview);

      return {
        ...prev,
        images: prev.images.filter((_, index) => index !== indexToRemove)
      };
    });
  };

  const insertImagePlaceholder = (imageName) => {
    if (!contentRef.current) return;

    const imageMarkdown = `![${imageName}](${imageName})\n`;
    const { start, end } = cursorPosition;

    const newContent =
      formData.content.substring(0, start) +
      imageMarkdown +
      formData.content.substring(end);

    setFormData(prev => ({
      ...prev,
      content: newContent
    }));

    // Update cursor position
    setTimeout(() => {
      if (contentRef.current) {
        const newPosition = start + imageMarkdown.length;
        contentRef.current.selectionStart = newPosition;
        contentRef.current.selectionEnd = newPosition;
        contentRef.current.focus();
      }
    }, 0);
  };

  // Text formatting helpers
  const insertFormatting = (prefix, suffix = '') => {
    if (!contentRef.current) return;

    const { start, end } = cursorPosition;
    const selectedText = formData.content.substring(start, end);

    const newContent =
      formData.content.substring(0, start) +
      prefix + selectedText + suffix +
      formData.content.substring(end);

    setFormData(prev => ({
      ...prev,
      content: newContent
    }));

    // Update cursor position and focus
    setTimeout(() => {
      if (contentRef.current) {
        const newPosition = selectedText.length > 0
          ? start + prefix.length + selectedText.length + suffix.length
          : start + prefix.length;
        contentRef.current.selectionStart = newPosition;
        contentRef.current.selectionEnd = newPosition;
        contentRef.current.focus();
      }
    }, 0);
  };



  const handleBoldClick = () => insertFormatting('**', '**');
  const handleItalicClick = () => insertFormatting('*', '*');
  const handleQuoteClick = () => insertFormatting('> ');
  const handleBulletListClick = () => insertFormatting('- ');
  const handleNumberedListClick = () => insertFormatting('1. ');

  const handleEditorTabChange = (event, newValue) => {
    setEditorTab(newValue);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }

    if (!formData.author.trim()) {
      newErrors.author = 'Author name is required';
    }

    if (!formData.category) {
      newErrors.category = 'Please select a category';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      enqueueSnackbar('Please fix the errors in the form', { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      // Process images if there are any
      const postDataToSend = { ...formData };

      // Process thumbnail if exists
      if (formData.thumbnail) {
        const thumbnailData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve({
              name: formData.thumbnail.name,
              data: reader.result, // Base64 encoded image
              type: formData.thumbnail.file.type
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(formData.thumbnail.file);
        });

        postDataToSend.thumbnailData = thumbnailData;
        // Remove the thumbnail field with File objects to avoid circular references
        delete postDataToSend.thumbnail;
      }

      // Process content images if there are any
      if (formData.images.length > 0) {
        postDataToSend.imageData = await Promise.all(
          formData.images.map(async (img) => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                resolve({
                  name: img.name,
                  data: reader.result, // Base64 encoded image
                  type: img.file.type
                });
              };
              reader.onerror = reject;
              reader.readAsDataURL(img.file);
            });
          })
        );

        // Remove the images field with File objects to avoid circular references
        delete postDataToSend.images;
      }

      await createPost(postDataToSend);
      enqueueSnackbar('Post created successfully', { variant: 'success' });
      navigate('/admin/posts');
    } catch (error) {
      console.error('Error creating post:', error);
      enqueueSnackbar('Failed to create post. Please try again.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Convert Markdown to HTML preview (basic implementation)
  const renderMarkdownPreview = () => {
    let html = formData.content
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Headings
      .replace(/^# (.*$)/gm, '<h1>$1</h1>')
      .replace(/^## (.*$)/gm, '<h2>$1</h2>')
      .replace(/^### (.*$)/gm, '<h3>$1</h3>')
      .replace(/^#### (.*$)/gm, '<h4>$1</h4>')
      // Blockquotes
      .replace(/^> (.*$)/gm, '<blockquote>$1</blockquote>')
      // Unordered lists
      .replace(/^- (.*$)/gm, '<li>$1</li>')
      // Ordered lists
      .replace(/^\d+\. (.*$)/gm, '<li>$1</li>')
      // Line breaks
      .replace(/\n/g, '<br />');

    // Replace image placeholders with actual images
    formData.images.forEach(img => {
      const placeholder = new RegExp(`!\\[${img.name}\\]\\(${img.name}\\)`, 'g');
      html = html.replace(placeholder, `<img src="${img.preview}" alt="${img.name}" style="max-width: 100%;" />`);
    });

    return { __html: html };
  };

  // Get category chip color based on category name
  const getCategoryColor = (category) => {
    const foundCategory = CATEGORIES.find(c => c.name === category);
    return foundCategory ? foundCategory.color : '#9e9e9e'; // Default to grey if not found
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 5 }}>
      <Box
        sx={{
          mb: 5,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Create New Post
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
            Share your scholarship and education insights with the WorldWise Scholar community
          </Typography>
        </Box>
        <Button
          variant="contained"
          disableElevation
          startIcon={<AddIcon />}
          sx={{
            px: 3,
            py: 1.2,
            fontSize: '1rem',
            fontWeight: 'medium',
            backgroundImage: 'linear-gradient(to right, #121B33, #1C2B4F)',
            borderRadius: 2
          }}
          onClick={() => {
            window.scrollTo({
              top: document.body.scrollHeight,
              behavior: 'smooth',
            });
          }}
        >
          Publish Post
        </Button>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            {/* Main Content Column */}
            <SectionBox>
              <SectionHeader>
                <TitleIcon fontSize="large" />
                <Typography variant="h6" fontWeight={600}>Post Details</Typography>
              </SectionHeader>

              {/* Title */}
              <TextField
                name="title"
                label="Post Title"
                placeholder="Enter an attention-grabbing title"
                fullWidth
                required
                value={formData.title}
                onChange={handleChange}
                error={Boolean(errors.title)}
                helperText={errors.title || "Make it descriptive and compelling"}
                sx={{ mb: 3 }}
                InputProps={{
                  sx: {
                    borderRadius: 1.5,
                    fontSize: '1.1rem',
                    py: 0.5
                  }
                }}
              />

              {/* Category Selection */}
              <FormControl
                fullWidth
                required
                error={Boolean(errors.category)}
                sx={{ mb: 3 }}
              >
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  name="category"
                  value={formData.category}
                  onChange={handleCategoryChange}
                  label="Category"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        component="span"
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          mr: 1.5,
                          backgroundColor: getCategoryColor(selected)
                        }}
                      />
                      {selected}
                    </Box>
                  )}
                  sx={{ borderRadius: 1.5 }}
                >
                  {CATEGORIES.map((category) => (
                    <MenuItem
                      key={category.name}
                      value={category.name}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: category.color,
                          mr: 2
                        }}
                      />
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && (
                  <FormHelperText>{errors.category}</FormHelperText>
                )}
              </FormControl>

              {/* Author */}
              <TextField
                name="author"
                label="Author"
                placeholder="Who is writing this post?"
                fullWidth
                required
                value={formData.author}
                onChange={handleChange}
                error={Boolean(errors.author)}
                helperText={errors.author}
                sx={{ mb: 3 }}
                InputProps={{
                  sx: { borderRadius: 1.5 }
                }}
              />

              {/* Rich Text Editor */}
              <Box sx={{ mt: 4 }}>
                <Box sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Tabs
                    value={editorTab}
                    onChange={handleEditorTabChange}
                    sx={{
                      '& .MuiTab-root': {
                        px: 3,
                        py: 1.5,
                        fontSize: '0.95rem',
                        fontWeight: 500,
                      }
                    }}
                  >
                    <Tab label="Write" />
                    <Tab label="Preview" />
                  </Tabs>

                  {editorTab === 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', p: 1 }}>

                      <Tooltip title="Bold">
                        <IconButton onClick={handleBoldClick} size="small">
                          <FormatBoldIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Italic">
                        <IconButton onClick={handleItalicClick} size="small">
                          <FormatItalicIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Quote">
                        <IconButton onClick={handleQuoteClick} size="small">
                          <FormatQuoteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Bullet List">
                        <IconButton onClick={handleBulletListClick} size="small">
                          <FormatListBulletedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Numbered List">
                        <IconButton onClick={handleNumberedListClick} size="small">
                          <FormatListNumberedIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>

                {/* Content Editor / Preview */}
                {editorTab === 0 ? (
                  <TextField
                    name="content"
                    placeholder="Start writing your post content here..."
                    multiline
                    rows={15}
                    fullWidth
                    required
                    value={formData.content}
                    onChange={handleChange}
                    inputRef={contentRef}
                    error={Boolean(errors.content)}
                    helperText={errors.content}
                    onSelect={() => {
                      if (contentRef.current) {
                        setCursorPosition({
                          start: contentRef.current.selectionStart,
                          end: contentRef.current.selectionEnd
                        });
                      }
                    }}
                    InputProps={{
                      sx: {
                        borderRadius: 1.5,
                        fontFamily: 'inherit',
                        fontSize: '1rem',
                        lineHeight: 1.6,
                        '& .MuiInputBase-input': {
                          fontFamily: 'inherit'
                        }
                      }
                    }}
                  />
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      minHeight: 300,
                      maxHeight: 500,
                      overflow: 'auto',
                      borderRadius: 1.5,
                      '& img': { maxWidth: '100%' },
                      '& h1, & h2, & h3, & h4': { mt: 2, mb: 1 },
                      '& blockquote': {
                        borderLeft: '3px solid #ccc',
                        pl: 2,
                        py: 1,
                        color: 'text.secondary',
                        fontStyle: 'italic'
                      },
                      '& li': {
                        ml: 3,
                        my: 1
                      }
                    }}
                  >
                    {formData.content ? (
                      <Box dangerouslySetInnerHTML={renderMarkdownPreview()} />
                    ) : (
                      <Typography color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', mt: 10 }}>
                        Content preview will appear here
                      </Typography>
                    )}
                  </Paper>
                )}
              </Box>
            </SectionBox>

            {/* Content Images Section */}
            <SectionBox>
              <SectionHeader>
                <ImageIcon fontSize="large" />
                <Typography variant="h6" fontWeight={600}>Content Images</Typography>
              </SectionHeader>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Upload images to include in your post content
                </Typography>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<ImageIcon />}
                  sx={{ mt: 1, borderRadius: 1.5 }}
                >
                  Upload Images
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                  />
                </Button>
              </Box>

              {formData.images.length > 0 && (
                <Grid container spacing={2}>
                  {formData.images.map((image, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card sx={{ boxShadow: 2, borderRadius: 2, overflow: 'hidden' }}>
                        <CardMedia
                          component="img"
                          height="140"
                          image={image.preview}
                          alt={image.name}
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardContent sx={{ py: 1.5, px: 2 }}>
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <Typography variant="caption" noWrap sx={{ maxWidth: '60%' }}>
                              {image.name}
                            </Typography>
                            <Box>
                              <Tooltip title="Insert into content">
                                <IconButton
                                  size="small"
                                  onClick={() => insertImagePlaceholder(image.name)}
                                  color="primary"
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove image">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveImage(index)}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {formData.images.length === 0 && (
                <Box sx={{
                  p: 3,
                  bgcolor: 'rgba(0,0,0,0.02)',
                  borderRadius: 2,
                  textAlign: 'center',
                  border: '1px dashed rgba(0,0,0,0.1)'
                }}>
                  <ImageIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    No images uploaded yet
                  </Typography>
                </Box>
              )}
            </SectionBox>
          </Grid>

          {/* Sidebar Column */}
          <Grid item xs={12} md={4}>
            {/* Thumbnail Section */}
            <SectionBox>
              <SectionHeader>
                <Typography variant="h6" fontWeight={600}>Featured Image</Typography>
              </SectionHeader>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  This image will be displayed as the post thumbnail
                </Typography>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<ImageIcon />}
                  fullWidth
                  sx={{ mt: 1, mb: 2, borderRadius: 1.5 }}
                >
                  Upload Thumbnail
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailUpload}
                  />
                </Button>

                {formData.thumbnail ? (
                  <Box sx={{ position: 'relative' }}>
                    <Card sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                      <CardMedia
                        component="img"
                        height="180"
                        image={formData.thumbnail.preview}
                        alt="Post thumbnail"
                        sx={{ objectFit: 'cover' }}
                      />
                      <CardContent sx={{ py: 1, px: 2 }}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {formData.thumbnail.name}
                        </Typography>
                      </CardContent>
                    </Card>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={handleRemoveThumbnail}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(255,255,255,0.9)',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,1)',
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{
                    p: 3,
                    bgcolor: 'rgba(0,0,0,0.02)',
                    borderRadius: 2,
                    textAlign: 'center',
                    border: '1px dashed rgba(0,0,0,0.1)',
                    height: 150,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <ImageIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      No thumbnail selected
                    </Typography>
                  </Box>
                )}
              </Box>
            </SectionBox>

            {/* Tags Section */}
            <SectionBox>
              <SectionHeader>
                <Typography variant="h6" fontWeight={600}>Post Tags</Typography>
              </SectionHeader>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                Add relevant tags to help readers discover your post
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 2 }}>
                <TextField
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleTagInputKeyPress}
                  placeholder="Add a tag"
                  size="small"
                  sx={{ mr: 1, flexGrow: 1 }}
                  InputProps={{
                    sx: { borderRadius: 1.5 },
                    endAdornment: tagSuggestionsLoading && (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  onClick={handleAddTag}
                  variant="contained"
                  disableElevation
                  sx={{ borderRadius: 1.5, px: 2 }}
                  disabled={!tagInput.trim()}
                >
                  Add
                </Button>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {formData.tags.length} tags added
                </Typography>
                <Tooltip title="Tag suggestions">
                  <IconButton
                    onClick={handleTagMenuOpen}
                    disabled={!suggestedTags.length && !COMMON_TAGS.length}
                    color={Boolean(tagAnchorEl) ? "primary" : "default"}
                    size="small"
                  >
                    <AutoAwesomeIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Menu
                anchorEl={tagAnchorEl}
                open={Boolean(tagAnchorEl)}
                onClose={handleTagMenuClose}
                PaperProps={{
                  sx: {
                    maxHeight: 300,
                    width: '250px',
                  }
                }}
              >
                {suggestedTags.length > 0 && (
                  <>
                    <Typography
                      variant="caption"
                      sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}
                    >
                      Generated suggestions
                    </Typography>
                    {suggestedTags.map((tag) => (
                      <MenuItem
                        key={tag}
                        onClick={() => handleSuggestedTagSelect(tag)}
                        sx={{ height: 36, px: 2 }}
                      >
                        {tag}
                      </MenuItem>
                    ))}
                    <Divider />
                  </>
                )}
                <Typography
                  variant="caption"
                  sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}
                >
                  Common tags
                </Typography>
                {COMMON_TAGS
                  .filter(tag => !formData.tags.includes(tag))
                  .map((tag) => (
                    <MenuItem
                      key={tag}
                      onClick={() => handleSuggestedTagSelect(tag)}
                      sx={{ height: 36, px: 2 }}
                    >
                      {tag}
                    </MenuItem>
                  ))
                }
              </Menu>

              {formData.tags.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleDeleteTag(tag)}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                  ))}
                </Box>
              ) : (
                <Box sx={{
                  p: 2,
                  bgcolor: 'rgba(0,0,0,0.02)',
                  borderRadius: 2,
                  textAlign: 'center',
                  border: '1px dashed rgba(0,0,0,0.1)'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    No tags added yet
                  </Typography>
                </Box>
              )}
            </SectionBox>

            {/* Publishing Section */}
            <SectionBox>
              <SectionHeader>
                <Typography variant="h6" fontWeight={600}>Publish Post</Typography>
              </SectionHeader>

              <Typography variant="body2" gutterBottom>
                Ready to share your post with the world?
              </Typography>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Button
                  type="button"
                  variant="outlined"
                  sx={{ borderRadius: 1.5, flexGrow: 1 }}
                  onClick={() => navigate('/admin/posts')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} color="inherit" />}
                  sx={{
                    borderRadius: 1.5,
                    flexGrow: 2,
                    py: 1.2,
                    backgroundImage: 'linear-gradient(to right, #121B33, #1C2B4F)'
                  }}
                >
                  {loading ? 'Creating...' : 'Publish Post'}
                </Button>
              </Box>
            </SectionBox>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default PostCreate;